import React, { useState, useEffect, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import SuperadminLayout from './SuperadminLayout';
import axios from '../../axios/axiosconf';
import './SuperadminStyles.css';

const NoticiaForm = () => {
    const history = useHistory();
    const { id } = useParams();
    const isEdit = Boolean(id);

    const [formData, setFormData] = useState({
        titulo: '',
        contenido: '',
        tipo: 'info',
        prioridad: 'media',
        fecha_inicio: new Date().toISOString().split('T')[0],
        fecha_fin: '',
        activo: true,
        mostrar_en_dashboard: true,
        icono: 'fas fa-info-circle',
        alcance: 'todos',
        clientes_ids: []
    });

    const [clientes, setClientes] = useState([]);
    const [busquedaCliente, setBusquedaCliente] = useState('');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({ show: false, type: '', message: '' });

    useEffect(() => {
        const token = localStorage.getItem('superadmin_token');
        const user = localStorage.getItem('superadmin_user');

        if (!token || !user) {
            history.push('/superadmin/login');
            return;
        }

        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        fetchClientes();

        if (isEdit) {
            fetchNoticia();
        }
    }, [history, id, isEdit]);

    const fetchClientes = async () => {
        try {
            const response = await axios.get('/superadmin/clientes');
            setClientes(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching clientes:', error);
            setClientes([]);
        }
    };

    const fetchNoticia = async () => {
        try {
            const response = await axios.get(`/superadmin/noticias/${id}`);
            const noticia = response.data;

            const formatearFecha = (fecha) => {
                if (!fecha) return '';
                return String(fecha).split(' ')[0];
            };

            const ids = Array.isArray(noticia.clientes_ids) ? noticia.clientes_ids.map(Number) : [];

            setFormData({
                titulo: noticia.titulo,
                contenido: noticia.contenido,
                tipo: noticia.tipo,
                prioridad: noticia.prioridad || 'media',
                fecha_inicio: formatearFecha(noticia.fecha_inicio),
                fecha_fin: formatearFecha(noticia.fecha_fin),
                activo: Boolean(noticia.activo),
                mostrar_en_dashboard: Boolean(noticia.mostrar_en_dashboard ?? true),
                icono: noticia.icono || 'fas fa-info-circle',
                alcance: ids.length > 0 ? 'especificos' : 'todos',
                clientes_ids: ids
            });
        } catch (error) {
            console.error('Error fetching noticia:', error);
            setAlert({
                show: true,
                type: 'danger',
                message: 'Error al cargar la noticia'
            });
        }
    };

    const clientesFiltrados = useMemo(() => {
        const q = busquedaCliente.trim().toLowerCase();
        if (!q) return clientes;
        return clientes.filter((c) =>
            String(c.nombre || '').toLowerCase().includes(q)
            || String(c.nit || '').toLowerCase().includes(q)
            || String(c.id).includes(q)
        );
    }, [clientes, busquedaCliente]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const toggleCliente = (clientId) => {
        const idNum = Number(clientId);
        setFormData((prev) => {
            const exists = prev.clientes_ids.includes(idNum);
            const next = exists
                ? prev.clientes_ids.filter((x) => x !== idNum)
                : [...prev.clientes_ids, idNum];
            return { ...prev, clientes_ids: next };
        });
        if (errors.clientes_ids) {
            setErrors((prev) => ({ ...prev, clientes_ids: '' }));
        }
    };

    const seleccionarTodosFiltrados = () => {
        const ids = clientesFiltrados.map((c) => Number(c.id));
        setFormData((prev) => ({
            ...prev,
            clientes_ids: Array.from(new Set([...prev.clientes_ids, ...ids]))
        }));
    };

    const limpiarSeleccion = () => {
        setFormData((prev) => ({ ...prev, clientes_ids: [] }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.titulo.trim()) {
            newErrors.titulo = 'El título es requerido';
        }

        if (!formData.contenido.trim()) {
            newErrors.contenido = 'El contenido es requerido';
        }

        if (!formData.fecha_inicio) {
            newErrors.fecha_inicio = 'La fecha de inicio es requerida';
        }

        if (formData.fecha_fin && formData.fecha_fin < formData.fecha_inicio) {
            newErrors.fecha_fin = 'La fecha fin debe ser posterior a la fecha de inicio';
        }

        if (formData.alcance === 'especificos' && formData.clientes_ids.length === 0) {
            newErrors.clientes_ids = 'Seleccione al menos un cliente, o elija "Todos"';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        const payload = {
            titulo: formData.titulo,
            contenido: formData.contenido,
            tipo: formData.tipo,
            prioridad: formData.prioridad,
            fecha_inicio: formData.fecha_inicio,
            fecha_fin: formData.fecha_fin || null,
            activo: formData.activo,
            mostrar_en_dashboard: formData.mostrar_en_dashboard,
            icono: formData.icono,
            clientes_ids: formData.alcance === 'especificos' ? formData.clientes_ids : null
        };

        try {
            if (isEdit) {
                await axios.put(`/superadmin/noticias/${id}`, payload);
                setAlert({
                    show: true,
                    type: 'success',
                    message: 'Noticia actualizada exitosamente'
                });
            } else {
                await axios.post('/superadmin/noticias', payload);
                setAlert({
                    show: true,
                    type: 'success',
                    message: 'Noticia creada exitosamente'
                });
            }

            setTimeout(() => {
                history.push('/superadmin/noticias');
            }, 1500);
        } catch (error) {
            console.error('Error saving noticia:', error);
            const message = error.response?.data?.message || 'Error al guardar la noticia';
            setAlert({
                show: true,
                type: 'danger',
                message: message
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <SuperadminLayout>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4>
                    <i className="fas fa-bullhorn"></i> {isEdit ? 'Editar Noticia' : 'Nueva Noticia'}
                </h4>
                <button
                    className="btn btn-secondary"
                    onClick={() => history.push('/superadmin/noticias')}
                >
                    <i className="fas fa-arrow-left"></i> Volver
                </button>
            </div>

            {alert.show && (
                <div className={`alert alert-${alert.type} alert-dismissible fade show`} role="alert">
                    {alert.message}
                    <button type="button" className="close" onClick={() => setAlert({ show: false, type: '', message: '' })}>
                        <span>&times;</span>
                    </button>
                </div>
            )}

            <div className="card">
                <div className="card-body">
                    <form onSubmit={handleSubmit}>
                        <div className="row">
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label>Título <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.titulo ? 'is-invalid' : ''}`}
                                        name="titulo"
                                        value={formData.titulo}
                                        onChange={handleChange}
                                        placeholder="Ej: Mantenimiento programado"
                                    />
                                    {errors.titulo && <div className="invalid-feedback">{errors.titulo}</div>}
                                </div>
                            </div>

                            <div className="col-md-3">
                                <div className="form-group">
                                    <label>Tipo <span className="text-danger">*</span></label>
                                    <select
                                        className="form-control"
                                        name="tipo"
                                        value={formData.tipo}
                                        onChange={handleChange}
                                    >
                                        <option value="info">Información (Azul)</option>
                                        <option value="secondary">Neutral (Gris)</option>
                                        <option value="light">Claro (Blanco)</option>
                                        <option value="dark">Oscuro (Negro)</option>
                                        <option value="warning">Advertencia (Amarillo)</option>
                                        <option value="danger">Problema (Rojo)</option>
                                        <option value="success">Éxito (Verde)</option>
                                    </select>
                                </div>
                            </div>

                            <div className="col-md-3">
                                <div className="form-group">
                                    <label>Prioridad <span className="text-danger">*</span></label>
                                    <select
                                        className="form-control"
                                        name="prioridad"
                                        value={formData.prioridad}
                                        onChange={handleChange}
                                    >
                                        <option value="baja">Baja</option>
                                        <option value="media">Media</option>
                                        <option value="alta">Alta</option>
                                        <option value="critica">Crítica</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Contenido <span className="text-danger">*</span></label>
                            <textarea
                                className={`form-control ${errors.contenido ? 'is-invalid' : ''}`}
                                name="contenido"
                                value={formData.contenido}
                                onChange={handleChange}
                                rows="5"
                                placeholder="Describe la noticia o anuncio..."
                            />
                            {errors.contenido && <div className="invalid-feedback">{errors.contenido}</div>}
                        </div>

                        <div className="row">
                            <div className="col-md-4">
                                <div className="form-group">
                                    <label>Fecha Inicio <span className="text-danger">*</span></label>
                                    <input
                                        type="date"
                                        className={`form-control ${errors.fecha_inicio ? 'is-invalid' : ''}`}
                                        name="fecha_inicio"
                                        value={formData.fecha_inicio}
                                        onChange={handleChange}
                                    />
                                    {errors.fecha_inicio && <div className="invalid-feedback">{errors.fecha_inicio}</div>}
                                </div>
                            </div>

                            <div className="col-md-4">
                                <div className="form-group">
                                    <label>Fecha Fin (Opcional)</label>
                                    <input
                                        type="date"
                                        className={`form-control ${errors.fecha_fin ? 'is-invalid' : ''}`}
                                        name="fecha_fin"
                                        value={formData.fecha_fin}
                                        onChange={handleChange}
                                    />
                                    {errors.fecha_fin && <div className="invalid-feedback">{errors.fecha_fin}</div>}
                                    <small className="form-text text-muted">
                                        Si no se especifica, la noticia se mostrará indefinidamente
                                    </small>
                                </div>
                            </div>

                            <div className="col-md-4">
                                <div className="form-group">
                                    <label className="d-block mb-2">Estado</label>
                                    <div className="d-flex flex-column" style={{ gap: '10px' }}>
                                        <label
                                            htmlFor="activo"
                                            className="mb-0 d-flex align-items-center"
                                            style={{ gap: '8px', cursor: 'pointer', userSelect: 'none' }}
                                        >
                                            <input
                                                type="checkbox"
                                                id="activo"
                                                name="activo"
                                                checked={formData.activo}
                                                onChange={handleChange}
                                                style={{ width: 16, height: 16, margin: 0, flexShrink: 0 }}
                                            />
                                            <span>{formData.activo ? 'Activa' : 'Inactiva'}</span>
                                        </label>
                                        <label
                                            htmlFor="mostrar_en_dashboard"
                                            className="mb-0 d-flex align-items-center"
                                            style={{ gap: '8px', cursor: 'pointer', userSelect: 'none' }}
                                        >
                                            <input
                                                type="checkbox"
                                                id="mostrar_en_dashboard"
                                                name="mostrar_en_dashboard"
                                                checked={formData.mostrar_en_dashboard}
                                                onChange={handleChange}
                                                style={{ width: 16, height: 16, margin: 0, flexShrink: 0 }}
                                            />
                                            <span>Mostrar en Dashboard</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card border mb-3">
                            <div className="card-header bg-light py-2">
                                <strong><i className="fas fa-users mr-2"></i>Alcance / Clientes</strong>
                            </div>
                            <div className="card-body">
                                <div className="form-group mb-3">
                                    <div className="custom-control custom-radio custom-control-inline">
                                        <input
                                            type="radio"
                                            id="alcance_todos"
                                            name="alcance"
                                            className="custom-control-input"
                                            value="todos"
                                            checked={formData.alcance === 'todos'}
                                            onChange={handleChange}
                                        />
                                        <label className="custom-control-label" htmlFor="alcance_todos">
                                            Todos los clientes
                                        </label>
                                    </div>
                                    <div className="custom-control custom-radio custom-control-inline">
                                        <input
                                            type="radio"
                                            id="alcance_especificos"
                                            name="alcance"
                                            className="custom-control-input"
                                            value="especificos"
                                            checked={formData.alcance === 'especificos'}
                                            onChange={handleChange}
                                        />
                                        <label className="custom-control-label" htmlFor="alcance_especificos">
                                            Clientes específicos
                                        </label>
                                    </div>
                                </div>

                                {formData.alcance === 'especificos' && (
                                    <>
                                        <div className="d-flex flex-wrap align-items-center mb-2" style={{ gap: '0.5rem' }}>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                style={{ maxWidth: 280 }}
                                                placeholder="Buscar cliente..."
                                                value={busquedaCliente}
                                                onChange={(e) => setBusquedaCliente(e.target.value)}
                                            />
                                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={seleccionarTodosFiltrados}>
                                                Seleccionar filtrados
                                            </button>
                                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={limpiarSeleccion}>
                                                Limpiar
                                            </button>
                                            <span className="small text-muted">
                                                {formData.clientes_ids.length} seleccionado(s)
                                            </span>
                                        </div>
                                        {errors.clientes_ids && (
                                            <div className="text-danger small mb-2">{errors.clientes_ids}</div>
                                        )}
                                        <div
                                            className="border rounded p-2"
                                            style={{ maxHeight: 220, overflowY: 'auto', background: '#fafbfc' }}
                                        >
                                            {clientesFiltrados.length === 0 ? (
                                                <p className="text-muted small mb-0">No hay clientes para mostrar.</p>
                                            ) : (
                                                clientesFiltrados.map((c) => (
                                                    <div className="custom-control custom-checkbox mb-1" key={c.id}>
                                                        <input
                                                            type="checkbox"
                                                            className="custom-control-input"
                                                            id={`cliente_${c.id}`}
                                                            checked={formData.clientes_ids.includes(Number(c.id))}
                                                            onChange={() => toggleCliente(c.id)}
                                                        />
                                                        <label className="custom-control-label" htmlFor={`cliente_${c.id}`}>
                                                            {c.nombre || `Cliente #${c.id}`}
                                                            <small className="text-muted ml-1">#{c.id}{c.nit ? ` · ${c.nit}` : ''}</small>
                                                        </label>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="form-group mt-2">
                            <button
                                type="submit"
                                className="btn btn-superadmin"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin"></i> Guardando...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-save"></i> {isEdit ? 'Actualizar' : 'Crear'} Noticia
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary ml-2"
                                onClick={() => history.push('/superadmin/noticias')}
                            >
                                Cancelar
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </SuperadminLayout>
    );
};

export default NoticiaForm;
